import {
  Component,
  ElementRef,
  computed,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormRoot, submit } from '@angular/forms/signals';

import { IdService } from '../../../services/id.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { FormAccessibilityAlertComponent } from '../accessibility-alert/form-accessibility-alert.component';
import { FormAccessibilityService } from '../accessibility-alert/form-accessibility.service';
import {
  FORM_SHELL,
  type FormAccessibilityScope,
  type FormShell,
  type FormShellStatus
} from '../form-shell.contract';

import type { OnDestroy, OnInit, Signal } from '@angular/core';
import type { FieldTree, ValidationError } from '@angular/forms/signals';
import type { AccessibleErrors, InputValidationError } from '../domain/form.domain';

@Component({
  selector: 'td-wb-signal-form',
  templateUrl: './signal-form.component.html',
  styleUrls: ['./signal-form.component.scss'],
  imports: [FormRoot, FormAccessibilityAlertComponent, TranslatePipe],
  providers: [
    { provide: FORM_SHELL, useExisting: SignalFormComponent }
  ]
})
export class SignalFormComponent<TModel = unknown> implements FormShell, OnInit, OnDestroy {
  private static readonly HEADER_ID_PREFIX = 'td-wb-signal-form-errors-aria-header';

  readonly formTree = input.required<FieldTree<TModel>>();
  readonly accessibilityScope = input<FormAccessibilityScope>('auto');
  private readonly submissionAttemptedState = signal(false);
  readonly submissionAttempted = this.submissionAttemptedState.asReadonly();
  readonly pristine = computed((): boolean => !this.formTree()().dirty());
  readonly valid = computed((): boolean => this.formTree()().valid());
  readonly invalid = computed((): boolean => this.formTree()().invalid());
  readonly pending = computed((): boolean => this.formTree()().pending());
  readonly submitting = computed((): boolean => this.formTree()().submitting());
  readonly accessibleHeaderId = inject(IdService)
    .generateNamespaceSequentialId(SignalFormComponent.HEADER_ID_PREFIX);

  private readonly nativeForm = viewChild.required<ElementRef<HTMLFormElement>>('nativeForm');
  private readonly formAccessibilityService = inject(FormAccessibilityService);
  private isModalForm = false;
  private readonly registeredErrors = signal<AccessibleErrors>({});

  readonly shellStatus: Signal<FormShellStatus> = computed((): FormShellStatus => {
    return {
      pristine: this.pristine(),
      valid: this.valid(),
      invalid: this.invalid(),
      pending: this.pending(),
      submissionAttempted: this.submissionAttempted(),
      submitting: this.submitting()
    };
  });

  protected readonly accessibleErrors = computed((): AccessibleErrors => ({
    ...this.createFieldTreeErrors(),
    ...this.registeredErrors()
  }));

  private readonly accessibleErrors$ = toObservable(this.accessibleErrors);

  ngOnInit(): void {
    this.isModalForm = this.accessibilityScope() === 'auto'
      ? this.formAccessibilityService.isModal()
      : this.accessibilityScope() === 'modal';
    this.formAccessibilityService.registerErrors(this.accessibleErrors$, this.isModalForm);
  }

  ngOnDestroy(): void {
    this.formAccessibilityService.unregisterErrors(this.isModalForm, this.accessibleErrors$);
  }

  requestSubmit(): void {
    this.nativeForm().nativeElement.requestSubmit();
  }

  async submit(): Promise<boolean> {
    this.submissionAttemptedState.set(true);
    const submitted = await submit(this.formTree());
    if (!submitted) {
      this.focusFirstError();
    }
    return submitted;
  }

  reset(): void {
    this.formTree()().reset();
    this.submissionAttemptedState.set(false);
  }

  setAccessibleError(inputName: string, error?: InputValidationError): void {
    const key = FormAccessibilityService.getErrorKey(inputName);
    this.registeredErrors.update((errors: AccessibleErrors): AccessibleErrors => {
      const updatedErrors = { ...errors };
      if (error === undefined) {
        delete updatedErrors[key];
      } else {
        updatedErrors[key] = error;
      }
      return updatedErrors;
    });
  }

  focusFirstError(): void {
    const firstError = Object.values(this.accessibleErrors())
      .find((error: InputValidationError): boolean => error.isDisplayed);
    if (firstError === undefined) {
      return;
    }
    if (firstError.isInViewPort()) {
      firstError.focusInput();
    } else {
      firstError.scrollIntoView();
    }
  }

  protected hasErrors(): boolean {
    return Object.values(this.accessibleErrors())
      .some((error: InputValidationError): boolean => error.isDisplayed);
  }

  protected onNativeSubmit(): void {
    this.submissionAttemptedState.set(true);
    queueMicrotask((): void => {
      if (this.formTree()().invalid()) {
        this.focusFirstError();
      }
    });
  }

  private createFieldTreeErrors(): AccessibleErrors {
    const errors: AccessibleErrors = {};
    this.formTree()().errorSummary().forEach((error: ValidationError.WithFieldTree, index: number): void => {
      const state = error.fieldTree();
      const inputName = state.name() || `field-${index}`;
      const key = `${FormAccessibilityService.getErrorKey(inputName)}-${index}`;
      const element = error.formField?.element;
      errors[key] = {
        key: error.kind,
        messageKey: error.message ?? error.kind,
        isDisplayed: this.submissionAttempted() || state.touched(),
        isInViewPort: (): boolean => this.isElementInViewport(element),
        scrollIntoView(): void {
          element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          state.focusBoundControl();
        },
        focusInput(): void {
          state.focusBoundControl();
        }
      };
    });
    return errors;
  }

  private isElementInViewport(element?: HTMLElement): boolean {
    if (element === undefined) {
      return true;
    }
    const rect = element.getBoundingClientRect();
    return rect.top >= 0 && rect.left >= 0 &&
      rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;
  }
}
